import React, {
  useRef,
  useState,
  useContext,
  useCallback,
  useEffect,
} from 'react';

import { AppContext } from '@prisjakt/ui-app-context';
import Search from '@prisjakt/ui-search';
import { Search as SearchInput } from '@prisjakt/ui-inputs';

import * as S from './styles';

const INPUT_MIN_LENGTH = 2;

const SEARCH_OPTIONS = {
  groups: [
    {
      limit: 5,
      name: 'stores',
      modes: ['store'],
    },
  ],
};

type Props = {
  showFloatingResult?: boolean;
  focusOnMount?: boolean;
  placeholder?: string;
  onSelect: any; // TODO: type
};

const ShopSearch = ({
  showFloatingResult = true,
  focusOnMount,
  placeholder,
  onSelect,
}: Props) => {
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { t } = useContext(AppContext);

  const [inputValue, setInputValueState] = useState('');
  const [search, setSearchState] = useState('');
  const [hasResultsState, setHasResultsState] = useState(false);
  const [results, setResultsState] = useState([]);

  const resetSearch = () => {
    setSearchState('');
    setHasResultsState(false);
  };

  const handleOnChange = useCallback(searchTerm => {
    setSearchState(searchTerm);
    setHasResultsState(false);
  }, []);

  const handleOnFocus = useCallback(searchTerm => {
    if (searchTerm.length >= INPUT_MIN_LENGTH) {
      setSearchState(searchTerm);
      setHasResultsState(true);
    }
  }, []);

  const setInputValue = useCallback(input => {
    setInputValueState(input);
  }, []);

  const handleOnSelect = useCallback(
    item => {
      setInputValueState('');
      resetSearch();

      return onSelect(item);
    },
    [onSelect]
  );

  const handleSearchResultFound = useCallback(items => {
    setHasResultsState(true);
    setResultsState(items[0]?.hits ?? []);
  }, []);

  const handleKeyDown = useCallback(e => {
    if (e.key === 'Escape') {
      resetSearch();
    }
  }, []);

  const handleClickOutside = useCallback(e => {
    if (searchRef.current && !searchRef.current.contains(e.target)) {
      resetSearch();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleClickOutside);

    if (focusOnMount && searchInputRef.current) {
      const inputElement: HTMLInputElement | null = searchInputRef.current.querySelector(
        'input[type=text]'
      );

      if (inputElement) {
        inputElement.focus();
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickOutside);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const searchIsOpen = inputValue.length >= INPUT_MIN_LENGTH && search !== '';
  const hasResults =
    hasResultsState && search !== '' && results && results.length > 0;

  return (
    <S.ShopSearch>
      <S.Text>
        <S.Title>
          {t('campaigns', 'shop-of-the-year.shop-search-title')}
        </S.Title>
        <p>{t('campaigns', 'shop-of-the-year.shop-search-text')}</p>
      </S.Text>

      <S.SearchWrapper ref={searchRef}>
        <S.Search>
          <S.SearchInput ref={searchInputRef} searchIsOpen={searchIsOpen}>
            <SearchInput
              key="search"
              placeholder={
                placeholder || t('search', 'search-shops.placeholder')
              }
              hasResults={hasResults}
              onChange={handleOnChange}
              onFocus={handleOnFocus}
              inputValue={inputValue}
              setInputValue={setInputValue}
            />
          </S.SearchInput>

          <S.SearchResult
            searchIsOpen={searchIsOpen}
            showFloatingResult={showFloatingResult}
          >
            <Search
              query={search}
              onSelect={handleOnSelect}
              showAllResults={false}
              keyboardNavigation={false}
              styling="rich"
              disableScrollToTop
              onSearchResultFound={handleSearchResultFound}
              options={SEARCH_OPTIONS}
              inputMinLength={INPUT_MIN_LENGTH}
              showStoreLogo
            />
          </S.SearchResult>
        </S.Search>
      </S.SearchWrapper>
    </S.ShopSearch>
  );
};

export default ShopSearch;
